import * as React from 'react';
import styled from 'styled-components';

import { Alert, Button } from 'components/bootstrap';
import { Icon } from 'components/common';
import { useModalContext } from 'common/contexts';
import useCurrentUser from 'hooks/useCurrentUser';
import { isPermitted } from 'util/PermissionsMixin';

import ProceduresList from './List';
import StepsList from './EventProcedureSteps/StepsList';

const Container = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const RoundedAlert = styled(Alert)`
  border-radius: 4px;
  padding: ${({ theme }) => `${theme.spacings.sm} ${theme.spacings.md}`};
  width: 100%;
  margin: 1.04rem 0 0.37rem 0;

  & .mantine-Alert-wrapper {
    flex-direction: row;
    align-items: center;
    width: 100%;
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;

function PageHeader({ viewType }: { viewType: string }) {
  const { setModal } = useModalContext();
  const { permissions } = useCurrentUser();
  const canCreateEventProcedures = React.useMemo(
    () => isPermitted(permissions, 'event_procedures:create'),
    [permissions],
  );
  const canCreateEventProcedureSteps = React.useMemo(
    () => isPermitted(permissions, 'event_procedure_steps:create'),
    [permissions],
  );

  const openEventProcedureModal = () => {
    setModal('CREATE-EVENT-PROCEDURE');
  };

  const openEventProcedureStepModal = () => {
    setModal('CREATE-EVENT-PROCEDURE-STEP');
  };

  const createButton = () => {
    if (viewType === 'procedures') {
      return (
        <Button bsStyle="primary" onClick={() => openEventProcedureModal()}>
          <Icon name="add" size="xs" />
          Create Event Procedure
        </Button>
      );
    }

    return (
      <Button bsStyle="primary" onClick={() => openEventProcedureStepModal()}>
        <Icon name="add" size="xs" />
        Create Event Procedure Step
      </Button>
    );
  };

  return (
    <Container>
      {((canCreateEventProcedures && viewType === 'procedures') ||
        (canCreateEventProcedureSteps && viewType === 'steps')) && <ButtonsRow>{createButton()}</ButtonsRow>}
    </Container>
  );
}

type Props = {
  viewType: string;
  useCoreRoutes?: boolean;
};

function GeneralUI({ viewType, useCoreRoutes = false }: Props) {
  return (
    <>
      {viewType === 'procedures' ? (
        <>
          <PageHeader viewType={viewType} />
          <RoundedAlert bsStyle="info">
            <span>
              We&apos;re excited to share this Early Access feature with you, and look forward to hearing how it supports your workflow. As we continue to improve the experience, please refer to our documentation for additional guidance and details.
            </span>
          </RoundedAlert>
          <ProceduresList useCoreRoutes={useCoreRoutes} />
        </>
      ) : (
        <>
          <PageHeader viewType={viewType} />
          <RoundedAlert bsStyle="info">
            <span>
              We&apos;re excited to share this Early Access feature with you, and look forward to hearing how it supports your workflow. As we continue to improve the experience, please refer to our documentation for additional guidance and details.
            </span>
          </RoundedAlert>
          <StepsList useCoreRoutes={useCoreRoutes} />
        </>
      )}
    </>
  );
}

export default GeneralUI;
